import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  applyUserPreferences, DEFAULT_PREFERENCES,
  ACCENT_OPTIONS, FONT_SIZE_OPTIONS, TOAST_POSITIONS, LANDING_OPTIONS,
} from "@/lib/preferences";
import { toast } from "sonner";

export default function SettingsDialog({ open, onOpenChange }) {
  const { refresh } = useAuth();
  const [prefs, setPrefs] = useState({ ...DEFAULT_PREFERENCES });
  const [original, setOriginal] = useState({ ...DEFAULT_PREFERENCES });

  useEffect(() => {
    if (!open) return;
    api.get("/users/me").then((r) => {
      const p = { ...DEFAULT_PREFERENCES, ...(r.data.preferences || {}) };
      setPrefs(p);
      setOriginal(p);
      applyUserPreferences(p); // live preview while open
    }).catch(() => {});
  }, [open]);

  const set = (k, v) => {
    const next = { ...prefs, [k]: v };
    setPrefs(next);
    applyUserPreferences(next); // sofortige Live-Vorschau
  };

  const save = async () => {
    try {
      await api.patch("/users/me/preferences", prefs);
      applyUserPreferences(prefs);
      await refresh?.();
      toast.success("Einstellungen gespeichert");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const cancel = () => {
    applyUserPreferences(original); // Rollback der Vorschau
    onOpenChange(false);
  };

  const reset = () => {
    setPrefs({ ...DEFAULT_PREFERENCES });
    applyUserPreferences({ ...DEFAULT_PREFERENCES });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : cancel())}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle>Persönliche Einstellungen</DialogTitle>
          <DialogDescription>
            Anpassungen werden sofort live in der Oberfläche angezeigt. Mit „Speichern" werden sie dauerhaft an deinem Benutzerkonto hinterlegt.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="appearance">
          <TabsList className="flex-wrap h-auto justify-start gap-1">
            <TabsTrigger value="appearance" data-testid="settings-tab-appearance">Erscheinungsbild</TabsTrigger>
            <TabsTrigger value="tables" data-testid="settings-tab-tables">Tabellen</TabsTrigger>
            <TabsTrigger value="locale" data-testid="settings-tab-locale">Sprache & Datum</TabsTrigger>
            <TabsTrigger value="notif" data-testid="settings-tab-notif">Benachrichtigungen</TabsTrigger>
            <TabsTrigger value="workflow" data-testid="settings-tab-workflow">Workflow</TabsTrigger>
          </TabsList>

          <TabsContent value="appearance" className="space-y-3 pt-4">
            <Row label="Erscheinungsbild">
              <Select value={prefs.theme} onValueChange={(v) => set("theme", v)}>
                <SelectTrigger data-testid="settings-theme"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Hell</SelectItem>
                  <SelectItem value="dark">Dunkel</SelectItem>
                  <SelectItem value="system">System (Automatisch)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Akzentfarbe">
              <div className="grid grid-cols-7 gap-2" data-testid="settings-accent-grid">
                {ACCENT_OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => set("accent_color", o.value)}
                    title={o.label}
                    data-testid={`accent-${o.value}`}
                    className={`h-9 rounded-md ring-2 ring-offset-2 transition-all ${
                      prefs.accent_color === o.value ? "ring-slate-900" : "ring-transparent"
                    }`}
                    style={{
                      backgroundColor: {
                        slate: "rgb(15 23 42)", teal: "rgb(20 184 166)", blue: "rgb(37 99 235)",
                        amber: "rgb(245 158 11)", rose: "rgb(244 63 94)", emerald: "rgb(16 185 129)",
                        purple: "rgb(168 85 247)",
                      }[o.value],
                    }}
                  />
                ))}
              </div>
            </Row>
            <Row label="Schriftgröße">
              <Select value={prefs.font_size} onValueChange={(v) => set("font_size", v)}>
                <SelectTrigger data-testid="settings-font-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FONT_SIZE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Anzeigedichte">
              <Select value={prefs.density} onValueChange={(v) => set("density", v)}>
                <SelectTrigger data-testid="settings-density"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="comfortable">Komfortabel</SelectItem>
                  <SelectItem value="compact">Kompakt</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Seitenleiste eingeklappt">
              <ToggleSwitch checked={prefs.sidebar_collapsed} onChange={(v) => set("sidebar_collapsed", v)} testid="settings-sidebar-collapsed" />
            </Row>
            <Row label="Avatare anzeigen">
              <ToggleSwitch checked={prefs.show_avatars} onChange={(v) => set("show_avatars", v)} testid="settings-show-avatars" />
            </Row>
          </TabsContent>

          <TabsContent value="tables" className="space-y-3 pt-4">
            <Row label="Einträge pro Tabelle">
              <Select value={String(prefs.table_page_size)} onValueChange={(v) => set("table_page_size", Number(v))}>
                <SelectTrigger data-testid="settings-table-size"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 25, 50, 100].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Zebrastreifen-Zeilen">
              <ToggleSwitch checked={prefs.table_zebra} onChange={(v) => set("table_zebra", v)} testid="settings-table-zebra" />
            </Row>
          </TabsContent>

          <TabsContent value="locale" className="space-y-3 pt-4">
            <Row label="Datumsformat">
              <Select value={prefs.date_format} onValueChange={(v) => set("date_format", v)}>
                <SelectTrigger data-testid="settings-date-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="TT.MM.JJJJ">TT.MM.JJJJ (Standard)</SelectItem>
                  <SelectItem value="TT. Monat JJJJ">TT. Monat JJJJ (lang)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Uhrzeit-Format">
              <Select value={prefs.time_format} onValueChange={(v) => set("time_format", v)}>
                <SelectTrigger data-testid="settings-time-format"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">24 Stunden (14:30)</SelectItem>
                  <SelectItem value="12h">12 Stunden (2:30 PM)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Wochenbeginn">
              <Select value={prefs.week_starts_on} onValueChange={(v) => set("week_starts_on", v)}>
                <SelectTrigger data-testid="settings-week-start"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monday">Montag</SelectItem>
                  <SelectItem value="sunday">Sonntag</SelectItem>
                </SelectContent>
              </Select>
            </Row>
          </TabsContent>

          <TabsContent value="notif" className="space-y-3 pt-4">
            <Row label="Position der Benachrichtigungen">
              <Select value={prefs.toast_position} onValueChange={(v) => set("toast_position", v)}>
                <SelectTrigger data-testid="settings-toast-position"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TOAST_POSITIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Anzeige­dauer">
              <Select value={String(prefs.toast_duration)} onValueChange={(v) => set("toast_duration", Number(v))}>
                <SelectTrigger data-testid="settings-toast-duration"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="2000">Kurz (2 Sek.)</SelectItem>
                  <SelectItem value="4000">Mittel (4 Sek.)</SelectItem>
                  <SelectItem value="7000">Lang (7 Sek.)</SelectItem>
                </SelectContent>
              </Select>
            </Row>
            <Row label="Hinweis-Töne">
              <ToggleSwitch checked={prefs.sound_enabled} onChange={(v) => set("sound_enabled", v)} testid="settings-sound" />
            </Row>
          </TabsContent>

          <TabsContent value="workflow" className="space-y-3 pt-4">
            <Row label="Startseite nach Anmeldung">
              <Select value={prefs.default_landing} onValueChange={(v) => set("default_landing", v)}>
                <SelectTrigger data-testid="settings-landing"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LANDING_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </Row>
            <Row label="Tastatur-Kürzel aktivieren">
              <ToggleSwitch checked={prefs.enable_keyboard_shortcuts} onChange={(v) => set("enable_keyboard_shortcuts", v)} testid="settings-shortcuts" />
            </Row>
            <Row label="Bei Löschen rückfragen">
              <ToggleSwitch checked={prefs.confirm_destructive} onChange={(v) => set("confirm_destructive", v)} testid="settings-confirm-destructive" />
            </Row>
          </TabsContent>

        </Tabs>

        <DialogFooter className="flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
          <Button variant="ghost" onClick={reset} data-testid="settings-reset-btn" className="w-full sm:w-auto">Auf Standard zurücksetzen</Button>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button variant="outline" onClick={cancel} className="flex-1 sm:flex-initial">Abbrechen</Button>
            <Button onClick={save} className="flex-1 sm:flex-initial bg-slate-900 hover:bg-slate-800 domivus-primary" data-accent-target="primary" data-testid="settings-save-btn">Speichern</Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 sm:items-center">
      <Label className="text-sm text-slate-700">{label}</Label>
      <div className="sm:col-span-2">{children}</div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? "bg-slate-900" : "bg-slate-300"
      }`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}
